import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to check user permissions
 * Returns utility functions to check if user has specific permissions
 */
export function usePermissions() {
  const { user } = useAuth();

  /**
   * Check if user has a specific permission
   * @param {string} resource - The resource name (e.g., 'customers', 'cashbook')
   * @param {string} action - The action name (e.g., 'view', 'add', 'edit', 'delete')
   * @returns {boolean} - True if user has the permission
   */
  const hasPermission = useMemo(() => {
    return (resource, action) => {
      if (user?.is_superuser) {
        return true;
      }

      if (!user?.permissions || user.permissions.length === 0) {
        return false;
      }

      const codename = `${resource}.${action}`;
      return user.permissions.includes(codename);
    };
  }, [user]);

  /**
   * Check if user has any of the specified permissions
   * @param {Array<[string, string]>} permissions - Array of [resource, action] tuples
   * @returns {boolean} - True if user has at least one permission
   */
  const hasAnyPermission = useMemo(() => {
    return (permissions) => {
      if (user?.is_superuser) {
        return true;
      }

      return permissions.some(([resource, action]) =>
        hasPermission(resource, action)
      );
    };
  }, [user, hasPermission]);

  /**
   * Check if user has all of the specified permissions
   * @param {Array<[string, string]>} permissions - Array of [resource, action] tuples
   * @returns {boolean} - True if user has all permissions
   */
  const hasAllPermissions = useMemo(() => {
    return (permissions) => {
      if (user?.is_superuser) {
        return true;
      }

      return permissions.every(([resource, action]) =>
        hasPermission(resource, action)
      );
    };
  }, [user, hasPermission]);

  /**
   * Check if user can view a resource
   * @param {string} resource - The resource name
   * @returns {boolean}
   */
  const canView = useMemo(() => {
    return (resource) => hasPermission(resource, 'view');
  }, [hasPermission]);

  /**
   * Check if user can add to a resource
   * @param {string} resource - The resource name
   * @returns {boolean}
   */
  const canAdd = useMemo(() => {
    return (resource) => hasPermission(resource, 'add');
  }, [hasPermission]);

  /**
   * Check if user can edit a resource
   * @param {string} resource - The resource name
   * @returns {boolean}
   */
  const canEdit = useMemo(() => {
    return (resource) => hasPermission(resource, 'edit');
  }, [hasPermission]);

  /**
   * Check if user can delete from a resource
   * @param {string} resource - The resource name
   * @returns {boolean}
   */
  const canDelete = useMemo(() => {
    return (resource) => hasPermission(resource, 'delete');
  }, [hasPermission]);

  /**
   * Check if user can manage a resource (full access)
   * @param {string} resource - The resource name
   * @returns {boolean}
   */
  const canManage = useMemo(() => {
    return (resource) => hasPermission(resource, 'manage');
  }, [hasPermission]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canView,
    canAdd,
    canEdit,
    canDelete,
    canManage,
    isSuperuser: user?.is_superuser || false,
    permissions: user?.permissions || [],
  };
}
